import {
  ItemRarity,
  RARITY_ROLL_WEIGHTS,
  RARITY_AFFIX_COUNT,
  TIER_DEFAULT_WEIGHTS,
} from '../constants';
import type { ItemModifiers, RolledAffixes } from '../types/item';
import { WeaponSubtype } from '../types/item';
import { KINGDOMS, type KingdomId } from './kingdoms';

/**
 * Rango (min, max) de un afijo dentro de un tier específico.
 * El roll del valor es uniforme; si `step` se define, se discretiza.
 */
export interface AffixTier {
  min: number;
  max: number;
  step?: number;
}

/**
 * Definición de un afijo rolleable. Cada afijo aplica una clave de
 * `ItemModifiers` con un valor random elegido en uno de 3 tiers:
 *  - tiers[0] = T1 (rango "peor")
 *  - tiers[1] = T2
 *  - tiers[2] = T3 (rango "mejor")
 *
 * Al rolear, primero se elige el tier (pesos en `tierWeights` o el default
 * `TIER_DEFAULT_WEIGHTS`), luego el valor dentro del rango del tier.
 *
 * `allowedSlots` restringe en qué slots de equipamiento puede rolear este
 * afijo. Si está vacío o undefined, puede rolear en cualquier slot.
 *
 * `placeholder: true` marca afijos cuyo modificador aún no está wired al
 * sistema de combate. Se rolean y muestran en tooltip con sufijo
 * "(próximamente)", pero no afectan stats hasta que se implementen.
 * Actualmente NINGÚN afijo lo usa (lifesteal/reflect/healingReceived ya están
 * wired); el campo queda como infra para afijos futuros que dependan de un
 * subsistema aún inexistente (p.ej. un `fishingBonus` cuando exista la Pesca).
 */
export interface AffixDefinition {
  id: string;
  modifierKey: keyof ItemModifiers;
  tiers: [AffixTier, AffixTier, AffixTier];
  allowedSlots?: string[];
  displayName: string;
  displayFormat?: 'flat' | 'percent';
  placeholder?: boolean;
  /** Pesos relativos de cada tier. Defaults a `TIER_DEFAULT_WEIGHTS`. */
  tierWeights?: readonly [number, number, number];
  /**
   * Duración (ms) del efecto que aplica este afijo, por tier [T1, T2, T3]. Solo
   * para afijos de proc de pérdida de movilidad (inmovilizar/paralizar): el valor
   * rolled es la CHANCE y la duración la define el tier. `loadPlayerEquipment` la
   * resuelve al armar el perfil de proc del player. Ver docs/CC_IMMOBILIZE_PARALYZE.md.
   */
  tierDurationsMs?: [number, number, number];
  /**
   * Reino objetivo, sólo para los afijos generados `aff_dmg_vs_<kingdom>`. Cuando
   * está seteado, el valor rolled va a `damageVsKingdom[kingdom]` (estructura
   * anidada) en vez del `modifierKey` plano, y el afijo se keyea por su `id` (no
   * por `modifierKey`) para no colisionar entre Reinos. Ver docs/CREATURE_KINGDOMS.md.
   */
  kingdom?: KingdomId;
  /**
   * Subtipo de arma objetivo, sólo para los afijos generados `aff_dmg_with_<subtype>`.
   * Análogo a `kingdom`: cuando está seteado, el valor rolled va a
   * `damageWithWeapon[subtype]` (anidado) y el afijo se keyea por su `id` (no por
   * `modifierKey`, que comparten todos). Ver docs/AFFIXES_AND_TIERS.md.
   */
  weaponSubtype?: WeaponSubtype;
  /**
   * Peso relativo de este afijo en la selección del pool (`rollAffixes`, vía
   * `pickWeightedAffix`). Default 1 → todos equiprobables (selección uniforme). Se
   * usa para que familias numerosas y de nicho no diluyan al resto del pool: los 16
   * afijos por Reino usan `KINGDOM_AFFIX_WEIGHT` (< 1) para competir como UNA familia
   * acotada en vez de 16 entradas de peso pleno. Ver docs/AFFIXES_AND_TIERS.md.
   */
  weight?: number;
}

// Slots agrupados para reutilizar en allowedSlots
const ARMOR_SLOTS = ['body', 'head', 'shield', 'boots'];
const ACCESSORY_SLOTS = ['ring', 'amulet', 'back'];
const WEAPON_SLOTS = ['weapon'];

/**
 * Familias de afijos POR REINO, generadas dinámicamente (una entrada por Reino en
 * `KINGDOMS`). Agregar un Reino nuevo al enum → sus afijos aparecen acá y en el pool
 * sin más código. Rolean sólo en accesorios (ACCESSORY_SLOTS) a propósito, para no
 * diluir demasiado los rolls de arma; tunable. Ver docs/CREATURE_KINGDOMS.md.
 *
 * `modifierKey` es un SENTINEL: nombra la key ANIDADA de `ItemModifiers` a la que va
 * el valor rolled (`damageVsKingdom[kingdom]` / `magicFindVsKingdom[kingdom]`), NO una
 * key plana. Por eso estos afijos se keyean por `id` en `RolledAffixes`.
 */
type KingdomNestedKey = 'damageVsKingdom' | 'magicFindVsKingdom';

/**
 * Peso de selección de CADA afijo por Reino en el pool de accesorios. < 1 a propósito:
 * son 16 (8 Reinos × {daño, MF}) afijos de nicho que, a peso pleno, ahogaban a los ~28
 * afijos "core" de accesorio (cada core caía de ~3.6% pre-Reinos a ~2.3%, −36% relativo;
 * y 3 Reinos reservados sin NPCs — demon/elemental/dragon — sumaban 6 rolls muertos ≈
 * 13.6% de los accesorios). Con 0.25 la familia entera ocupa ~12.5% de los rolls de
 * accesorio (16×0.25 / (28 + 16×0.25) = 4/32) y cada core recupera ~3.1%, cerca del
 * baseline previo. Subir/bajar este único número re-tunea toda la familia (reversible).
 * Ver docs/AFFIXES_AND_TIERS.md.
 */
const KINGDOM_AFFIX_WEIGHT = 0.25;

function makeKingdomAffixes(
  nestedKey: KingdomNestedKey,
  idPrefix: string,
  displayPrefix: string,
  tiers: [AffixTier, AffixTier, AffixTier]
): AffixDefinition[] {
  return KINGDOMS.map((k) => ({
    id: `${idPrefix}${k.id}`,
    modifierKey: nestedKey as keyof ItemModifiers,
    kingdom: k.id,
    tiers,
    allowedSlots: ACCESSORY_SLOTS,
    displayName: `${displayPrefix} ${k.labelEsPlural}`,
    displayFormat: 'percent' as const,
    weight: KINGDOM_AFFIX_WEIGHT,
  }));
}

const KINGDOM_DAMAGE_AFFIXES: AffixDefinition[] = makeKingdomAffixes(
  'damageVsKingdom',
  'aff_dmg_vs_',
  'Daño vs',
  [
    { min: 0.05, max: 0.08, step: 0.01 },
    { min: 0.09, max: 0.13, step: 0.01 },
    { min: 0.14, max: 0.2, step: 0.01 },
  ]
);

const KINGDOM_MF_AFFIXES: AffixDefinition[] = makeKingdomAffixes(
  'magicFindVsKingdom',
  'aff_mf_vs_',
  'Rareza vs',
  [
    { min: 0.08, max: 0.15, step: 0.01 },
    { min: 0.16, max: 0.28, step: 0.01 },
    { min: 0.29, max: 0.45, step: 0.01 },
  ]
);

/** Lookup id de afijo de Reino → { Reino, key anidada } (para flatten/display). */
const KINGDOM_AFFIX_BY_ID: ReadonlyMap<string, { kingdom: KingdomId; nestedKey: KingdomNestedKey }> =
  new Map(
    [...KINGDOM_DAMAGE_AFFIXES, ...KINGDOM_MF_AFFIXES].map((a) => [
      a.id,
      { kingdom: a.kingdom as KingdomId, nestedKey: a.modifierKey as KingdomNestedKey },
    ])
  );

/**
 * Familia de afijos POR TIPO DE ARMA (`aff_dmg_with_<subtype>`): +% daño físico con ese
 * tipo de arma. Sólo subtipos FÍSICOS (no staff/wand). Rolean en armas + armaduras +
 * accesorios (habilitan builds "de un arma" desde cualquier pieza). Mismo mecanismo
 * anidado que los afijos de Reino: `modifierKey = 'damageWithWeapon'` es un SENTINEL y
 * el valor va a `damageWithWeapon[subtype]`; se keyean por `id`. Peso reducido para no
 * diluir el pool (son 8 afijos de nicho). Ver docs/AFFIXES_AND_TIERS.md.
 */
const PHYSICAL_WEAPON_SUBTYPES: { sub: WeaponSubtype; labelPlural: string }[] = [
  { sub: WeaponSubtype.SWORD, labelPlural: 'Espadas' },
  { sub: WeaponSubtype.AXE, labelPlural: 'Hachas' },
  { sub: WeaponSubtype.MACE, labelPlural: 'Mazas' },
  { sub: WeaponSubtype.DAGGER, labelPlural: 'Dagas' },
  { sub: WeaponSubtype.KNUCKLES, labelPlural: 'Nudilleras' },
  { sub: WeaponSubtype.BOW, labelPlural: 'Arcos' },
  { sub: WeaponSubtype.CROSSBOW, labelPlural: 'Ballestas' },
  { sub: WeaponSubtype.THROWABLE, labelPlural: 'Arrojadizas' },
];

/** Peso de selección de cada afijo de arma en el pool (< 1: familia acotada). */
const WEAPON_AFFIX_WEIGHT = 0.35;

const WEAPON_DAMAGE_AFFIXES: AffixDefinition[] = PHYSICAL_WEAPON_SUBTYPES.map(({ sub, labelPlural }) => ({
  id: `aff_dmg_with_${sub}`,
  modifierKey: 'damageWithWeapon' as keyof ItemModifiers,
  weaponSubtype: sub,
  tiers: [
    { min: 0.04, max: 0.07, step: 0.01 },
    { min: 0.08, max: 0.12, step: 0.01 },
    { min: 0.13, max: 0.2, step: 0.01 },
  ] as [AffixTier, AffixTier, AffixTier],
  // Todos los slots equipables (armas + armaduras + accesorios).
  allowedSlots: [...WEAPON_SLOTS, ...ARMOR_SLOTS, ...ACCESSORY_SLOTS],
  displayName: `Daño con ${labelPlural}`,
  displayFormat: 'percent' as const,
  weight: WEAPON_AFFIX_WEIGHT,
}));

/** Lookup id de afijo de arma → subtipo (para flatten a damageWithWeapon[subtype]). */
const WEAPON_AFFIX_BY_ID: ReadonlyMap<string, WeaponSubtype> = new Map(
  WEAPON_DAMAGE_AFFIXES.map((a) => [a.id, a.weaponSubtype as WeaponSubtype])
);

/**
 * Pool global de afijos rolleables. Cada drop RARE/EPIC selecciona N afijos
 * de este pool (filtrando por slot del item) y, para cada uno, rolea su tier
 * y valor independientemente.
 *
 * Tiers diseñados así: T2 preserva ~el valor base "antiguo"; T1 es ~40% más
 * débil; T3 es ~50% más fuerte. Ajustar a gusto.
 */
export const AFFIX_POOL: AffixDefinition[] = [
  // ===== Vida / Mana / Stamina (universales) =====
  {
    id: 'aff_hp', modifierKey: 'hp',
    tiers: [{ min: 5, max: 12, step: 1 }, { min: 13, max: 22, step: 1 }, { min: 23, max: 35, step: 1 }],
    displayName: 'HP', displayFormat: 'flat',
  },
  {
    id: 'aff_mana', modifierKey: 'mana',
    tiers: [{ min: 5, max: 12, step: 1 }, { min: 13, max: 22, step: 1 }, { min: 23, max: 35, step: 1 }],
    displayName: 'Maná', displayFormat: 'flat',
  },
  {
    id: 'aff_stamina', modifierKey: 'stamina',
    tiers: [{ min: 4, max: 10, step: 1 }, { min: 11, max: 18, step: 1 }, { min: 19, max: 30, step: 1 }],
    displayName: 'Stamina', displayFormat: 'flat',
  },

  // ===== Defensas =====
  {
    id: 'aff_physdef', modifierKey: 'physDef',
    tiers: [{ min: 1, max: 3, step: 1 }, { min: 4, max: 6, step: 1 }, { min: 7, max: 10, step: 1 }],
    allowedSlots: [...ARMOR_SLOTS, ...ACCESSORY_SLOTS],
    displayName: 'Def. Física', displayFormat: 'flat',
  },
  {
    id: 'aff_magicdef', modifierKey: 'magicDef',
    tiers: [{ min: 1, max: 3, step: 1 }, { min: 4, max: 6, step: 1 }, { min: 7, max: 10, step: 1 }],
    allowedSlots: [...ARMOR_SLOTS, ...ACCESSORY_SLOTS],
    displayName: 'Def. Mágica', displayFormat: 'flat',
  },

  // ===== Daño (sólo armas) =====
  {
    id: 'aff_min_damage', modifierKey: 'minDamage',
    tiers: [{ min: 1, max: 1, step: 1 }, { min: 2, max: 2, step: 1 }, { min: 3, max: 4, step: 1 }],
    allowedSlots: WEAPON_SLOTS,
    displayName: 'Daño Mín.', displayFormat: 'flat',
  },
  {
    id: 'aff_max_damage', modifierKey: 'maxDamage',
    tiers: [{ min: 2, max: 3, step: 1 }, { min: 4, max: 5, step: 1 }, { min: 6, max: 8, step: 1 }],
    allowedSlots: WEAPON_SLOTS,
    displayName: 'Daño Máx.', displayFormat: 'flat',
  },

  // ===== Velocidades =====
  {
    id: 'aff_movespeed', modifierKey: 'moveSpeed',
    tiers: [{ min: 0.03, max: 0.07, step: 0.01 }, { min: 0.08, max: 0.12, step: 0.01 }, { min: 0.13, max: 0.18, step: 0.01 }],
    allowedSlots: ['boots', 'ring', 'amulet'],
    displayName: 'Velocidad de Movimiento', displayFormat: 'flat',
  },
  {
    id: 'aff_attackspeed', modifierKey: 'attackSpeed',
    tiers: [{ min: 0.03, max: 0.07, step: 0.01 }, { min: 0.08, max: 0.12, step: 0.01 }, { min: 0.13, max: 0.18, step: 0.01 }],
    allowedSlots: [...WEAPON_SLOTS, 'ring', 'amulet'],
    displayName: 'Velocidad de Ataque', displayFormat: 'flat',
  },
  {
    id: 'aff_castspeed', modifierKey: 'castSpeed',
    tiers: [{ min: 0.03, max: 0.07, step: 0.01 }, { min: 0.08, max: 0.12, step: 0.01 }, { min: 0.13, max: 0.18, step: 0.01 }],
    allowedSlots: [...WEAPON_SLOTS, ...ACCESSORY_SLOTS],
    displayName: 'Velocidad de Casteo', displayFormat: 'flat',
  },
  {
    // Extiende el rango MÁXIMO y reduce el MÍNIMO de los hechizos (tiles). Enteros
    // fijos por tier. Lo consume SpellSystem.checkSpellRange. Ver docs/SPELLS.md.
    id: 'aff_spell_range', modifierKey: 'spellRangeBonus',
    tiers: [{ min: 1, max: 1 }, { min: 2, max: 2 }, { min: 3, max: 3 }],
    allowedSlots: [...WEAPON_SLOTS, ...ACCESSORY_SLOTS],
    displayName: 'Alcance de Hechizos', displayFormat: 'flat',
  },

  // ===== Invocaciones =====
  {
    // Aumenta el límite de invocaciones simultáneas del jugador: +1/+2/+3 por tier
    // (valor fijo por tier, mismo molde que aff_spell_range). Sólo accesorios (stat de
    // summoner). El server lo consume en SummonSystem.getSummonLimit. Ver docs/SUMMONS.md.
    id: 'aff_summon_limit', modifierKey: 'summonLimit',
    tiers: [{ min: 1, max: 1 }, { min: 2, max: 2 }, { min: 3, max: 3 }],
    allowedSlots: [...ACCESSORY_SLOTS],
    displayName: 'Límite de Invocaciones', displayFormat: 'flat',
  },

  // ===== Críticos =====
  {
    id: 'aff_critchance', modifierKey: 'critChance',
    tiers: [{ min: 0.01, max: 0.03, step: 0.01 }, { min: 0.04, max: 0.06, step: 0.01 }, { min: 0.07, max: 0.10, step: 0.01 }],
    allowedSlots: [...WEAPON_SLOTS, ...ACCESSORY_SLOTS],
    displayName: 'Crítico', displayFormat: 'percent',
  },
  {
    id: 'aff_critmult', modifierKey: 'critMultiplier',
    tiers: [{ min: 0.05, max: 0.12, step: 0.01 }, { min: 0.13, max: 0.22, step: 0.01 }, { min: 0.23, max: 0.35, step: 0.01 }],
    allowedSlots: [...WEAPON_SLOTS, ...ACCESSORY_SLOTS],
    displayName: 'Mult. Crítico', displayFormat: 'percent',
  },

  // ===== Evasión =====
  {
    id: 'aff_evasion', modifierKey: 'evasion',
    tiers: [{ min: 0.01, max: 0.03, step: 0.01 }, { min: 0.04, max: 0.06, step: 0.01 }, { min: 0.07, max: 0.10, step: 0.01 }],
    allowedSlots: ['body', 'boots', 'ring', 'amulet'],
    displayName: 'Evasión', displayFormat: 'percent',
  },

  // ===== Regeneración =====
  {
    id: 'aff_regen_hp', modifierKey: 'regenHP',
    tiers: [{ min: 0.05, max: 0.12, step: 0.01 }, { min: 0.13, max: 0.22, step: 0.01 }, { min: 0.23, max: 0.35, step: 0.01 }],
    displayName: 'Regen HP', displayFormat: 'flat',
  },
  {
    id: 'aff_regen_mana', modifierKey: 'regenMana',
    tiers: [{ min: 0.05, max: 0.12, step: 0.01 }, { min: 0.13, max: 0.22, step: 0.01 }, { min: 0.23, max: 0.35, step: 0.01 }],
    displayName: 'Regen Maná', displayFormat: 'flat',
  },
  {
    id: 'aff_regen_stamina', modifierKey: 'regenStamina',
    tiers: [{ min: 0.05, max: 0.12, step: 0.01 }, { min: 0.13, max: 0.22, step: 0.01 }, { min: 0.23, max: 0.35, step: 0.01 }],
    displayName: 'Regen Stamina', displayFormat: 'flat',
  },

  // ===== Porcentuales =====
  {
    id: 'aff_hp_pct', modifierKey: 'hpPercent',
    tiers: [{ min: 0.03, max: 0.06, step: 0.01 }, { min: 0.07, max: 0.12, step: 0.01 }, { min: 0.13, max: 0.20, step: 0.01 }],
    displayName: 'HP', displayFormat: 'percent',
  },
  {
    id: 'aff_mana_pct', modifierKey: 'manaPercent',
    tiers: [{ min: 0.03, max: 0.06, step: 0.01 }, { min: 0.07, max: 0.12, step: 0.01 }, { min: 0.13, max: 0.20, step: 0.01 }],
    displayName: 'Maná', displayFormat: 'percent',
  },
  {
    id: 'aff_stamina_pct', modifierKey: 'staminaPercent',
    tiers: [{ min: 0.03, max: 0.06, step: 0.01 }, { min: 0.07, max: 0.12, step: 0.01 }, { min: 0.13, max: 0.20, step: 0.01 }],
    displayName: 'Stamina', displayFormat: 'percent',
  },
  {
    id: 'aff_damage_pct', modifierKey: 'damagePercent',
    tiers: [{ min: 0.04, max: 0.08, step: 0.01 }, { min: 0.09, max: 0.15, step: 0.01 }, { min: 0.16, max: 0.25, step: 0.01 }],
    allowedSlots: WEAPON_SLOTS,
    displayName: 'Daño', displayFormat: 'percent',
  },
  {
    id: 'aff_attackspeed_pct', modifierKey: 'attackSpeedPercent',
    tiers: [{ min: 0.03, max: 0.06, step: 0.01 }, { min: 0.07, max: 0.12, step: 0.01 }, { min: 0.13, max: 0.20, step: 0.01 }],
    allowedSlots: [...WEAPON_SLOTS, ...ACCESSORY_SLOTS],
    displayName: 'Velocidad de Ataque', displayFormat: 'percent',
  },
  {
    id: 'aff_movespeed_pct', modifierKey: 'moveSpeedPercent',
    tiers: [{ min: 0.03, max: 0.06, step: 0.01 }, { min: 0.07, max: 0.12, step: 0.01 }, { min: 0.13, max: 0.20, step: 0.01 }],
    allowedSlots: ['boots', ...ACCESSORY_SLOTS],
    displayName: 'Velocidad de Movimiento', displayFormat: 'percent',
  },
  {
    id: 'aff_critchance_pct', modifierKey: 'critChancePercent',
    tiers: [{ min: 0.02, max: 0.04, step: 0.01 }, { min: 0.05, max: 0.08, step: 0.01 }, { min: 0.09, max: 0.14, step: 0.01 }],
    allowedSlots: [...WEAPON_SLOTS, ...ACCESSORY_SLOTS],
    displayName: 'Crítico', displayFormat: 'percent',
  },
  {
    id: 'aff_critmult_pct', modifierKey: 'critMultiplierPercent',
    tiers: [{ min: 0.05, max: 0.10, step: 0.01 }, { min: 0.11, max: 0.20, step: 0.01 }, { min: 0.21, max: 0.35, step: 0.01 }],
    allowedSlots: [...WEAPON_SLOTS, ...ACCESSORY_SLOTS],
    displayName: 'Mult. Crítico', displayFormat: 'percent',
  },

  // ===== Magic Find (sólo accesorios) =====
  {
    id: 'aff_magic_find', modifierKey: 'magicFind',
    tiers: [{ min: 0.05, max: 0.10, step: 0.01 }, { min: 0.11, max: 0.20, step: 0.01 }, { min: 0.21, max: 0.35, step: 0.01 }],
    allowedSlots: ACCESSORY_SLOTS,
    displayName: 'Magic Find', displayFormat: 'percent',
  },

  // ===== Reactivos de combate (wired: lifesteal/reflect en CombatHooks, healingReceived en SpellSystem) =====
  {
    id: 'aff_lifesteal_phys', modifierKey: 'lifeStealPhysical',
    tiers: [{ min: 0.01, max: 0.02, step: 0.005 }, { min: 0.025, max: 0.04, step: 0.005 }, { min: 0.045, max: 0.06, step: 0.005 }],
    allowedSlots: WEAPON_SLOTS,
    displayName: 'Robo de Vida (físico)', displayFormat: 'percent',
  },
  {
    id: 'aff_lifesteal_magic', modifierKey: 'lifeStealMagic',
    tiers: [{ min: 0.01, max: 0.02, step: 0.005 }, { min: 0.025, max: 0.04, step: 0.005 }, { min: 0.045, max: 0.06, step: 0.005 }],
    allowedSlots: WEAPON_SLOTS,
    displayName: 'Robo de Vida (mágico)', displayFormat: 'percent',
  },
  {
    id: 'aff_reflect_phys', modifierKey: 'reflectPhysicalDamage',
    tiers: [{ min: 0.03, max: 0.05, step: 0.01 }, { min: 0.06, max: 0.08, step: 0.01 }, { min: 0.09, max: 0.12, step: 0.01 }],
    allowedSlots: ARMOR_SLOTS,
    displayName: 'Reflejar Daño Físico', displayFormat: 'percent',
  },
  {
    id: 'aff_reflect_magic', modifierKey: 'reflectMagicDamage',
    tiers: [{ min: 0.03, max: 0.05, step: 0.01 }, { min: 0.06, max: 0.08, step: 0.01 }, { min: 0.09, max: 0.12, step: 0.01 }],
    allowedSlots: ARMOR_SLOTS,
    displayName: 'Reflejar Daño Mágico', displayFormat: 'percent',
  },
  {
    id: 'aff_healing_received', modifierKey: 'healingReceived',
    tiers: [{ min: 0.05, max: 0.10, step: 0.01 }, { min: 0.11, max: 0.20, step: 0.01 }, { min: 0.21, max: 0.35, step: 0.01 }],
    allowedSlots: ACCESSORY_SLOTS,
    displayName: 'Curación Recibida', displayFormat: 'percent',
  },

  // ===== Pérdida de movilidad (procs ofensivos: chance + duración por tier) =====
  // El valor rolled es la CHANCE; la duración la define el tier (tierDurationsMs).
  // Ver docs/CC_IMMOBILIZE_PARALYZE.md.
  {
    id: 'aff_immobilize_on_hit_physical', modifierKey: 'immobilizeOnHitPhysical',
    tiers: [{ min: 0.04, max: 0.06, step: 0.01 }, { min: 0.07, max: 0.10, step: 0.01 }, { min: 0.11, max: 0.15, step: 0.01 }],
    tierDurationsMs: [1500, 2200, 3000],
    allowedSlots: WEAPON_SLOTS,
    displayName: 'Inmovilizar al golpear', displayFormat: 'percent',
  },
  {
    id: 'aff_immobilize_on_hit_spell', modifierKey: 'immobilizeOnHitSpell',
    tiers: [{ min: 0.04, max: 0.06, step: 0.01 }, { min: 0.07, max: 0.10, step: 0.01 }, { min: 0.11, max: 0.15, step: 0.01 }],
    tierDurationsMs: [1500, 2200, 3000],
    allowedSlots: [...WEAPON_SLOTS, ...ACCESSORY_SLOTS],
    displayName: 'Inmovilizar con hechizo', displayFormat: 'percent',
  },
  {
    id: 'aff_paralyze_on_hit_physical', modifierKey: 'paralyzeOnHitPhysical',
    tiers: [{ min: 0.02, max: 0.03, step: 0.01 }, { min: 0.04, max: 0.06, step: 0.01 }, { min: 0.07, max: 0.10, step: 0.01 }],
    tierDurationsMs: [1000, 1400, 1800],
    allowedSlots: WEAPON_SLOTS,
    displayName: 'Paralizar al golpear', displayFormat: 'percent',
  },
  {
    id: 'aff_paralyze_on_hit_spell', modifierKey: 'paralyzeOnHitSpell',
    tiers: [{ min: 0.02, max: 0.03, step: 0.01 }, { min: 0.04, max: 0.06, step: 0.01 }, { min: 0.07, max: 0.10, step: 0.01 }],
    tierDurationsMs: [1000, 1400, 1800],
    allowedSlots: [...WEAPON_SLOTS, ...ACCESSORY_SLOTS],
    displayName: 'Paralizar con hechizo', displayFormat: 'percent',
  },

  // ===== Reducción defensiva de duración de pérdida de movilidad =====
  {
    id: 'aff_immobilize_dr', modifierKey: 'immobilizeDurationReduction',
    tiers: [{ min: 0.05, max: 0.10, step: 0.01 }, { min: 0.11, max: 0.18, step: 0.01 }, { min: 0.19, max: 0.30, step: 0.01 }],
    allowedSlots: [...ARMOR_SLOTS, ...ACCESSORY_SLOTS],
    displayName: 'Reducción de Inmovilizar', displayFormat: 'percent',
  },
  {
    id: 'aff_paralyze_dr', modifierKey: 'paralyzeDurationReduction',
    tiers: [{ min: 0.05, max: 0.10, step: 0.01 }, { min: 0.11, max: 0.18, step: 0.01 }, { min: 0.19, max: 0.30, step: 0.01 }],
    allowedSlots: [...ARMOR_SLOTS, ...ACCESSORY_SLOTS],
    displayName: 'Reducción de Paralizar', displayFormat: 'percent',
  },

  // ===== Por Reino (generados dinámicamente, uno por Reino y familia) =====
  ...KINGDOM_DAMAGE_AFFIXES,
  ...KINGDOM_MF_AFFIXES,

  // ===== Por tipo de arma (+% daño con X arma; generados por subtipo físico) =====
  ...WEAPON_DAMAGE_AFFIXES,
];

/**
 * Rolea la rareza para una instancia (templates con `affixable: true`).
 * Pesos por defecto en `RARITY_ROLL_WEIGHTS` (90/9/1).
 *
 * `magicFindBonus` escala proporcionalmente la chance de RARE+EPIC manteniendo
 * la relación entre ellas. Ej: con bonus 0.5 (+50% MF), la chance no-COMMON
 * sube de 0.10 → 0.15, repartida 0.135 RARE + 0.015 EPIC.
 */
export function rollRarity(magicFindBonus = 0): ItemRarity {
  const rareW = RARITY_ROLL_WEIGHTS[ItemRarity.RARE];
  const epicW = RARITY_ROLL_WEIGHTS[ItemRarity.EPIC];

  const nonCommonBase = rareW + epicW;
  const nonCommonNew = Math.min(1, Math.max(0, nonCommonBase * (1 + magicFindBonus)));

  // Si MF == 0, esto colapsa a la tabla original.
  const r = Math.random();
  const commonNew = 1 - nonCommonNew;
  if (r < commonNew) return ItemRarity.COMMON;
  // Repartir el non-common entre RARE/EPIC en la misma proporción que la base.
  const ratioRare = rareW / nonCommonBase; // 0.9
  const rareNew = nonCommonNew * ratioRare;
  if (r < commonNew + rareNew) return ItemRarity.RARE;
  return ItemRarity.EPIC;
  // Nota: el cálculo asegura que con MF=0 → exactamente 90/9/1 (es decir,
  // commonNew = 0.9, rareNew = 0.09, epic = 0.01).
}

/**
 * Rolea cuál tier (1/2/3) le toca a un afijo, según `aff.tierWeights` o el
 * default `TIER_DEFAULT_WEIGHTS`.
 */
export function rollAffixTier(aff: AffixDefinition): 1 | 2 | 3 {
  const weights = aff.tierWeights ?? TIER_DEFAULT_WEIGHTS;
  const total = weights[0] + weights[1] + weights[2];
  const r = Math.random() * total;
  if (r < weights[0]) return 1;
  if (r < weights[0] + weights[1]) return 2;
  return 3;
}

/**
 * Rolea un valor uniformemente dentro del rango del tier dado, respetando
 * el step (si está definido). El tier es 1-indexed (1/2/3).
 */
export function rollAffixValueForTier(aff: AffixDefinition, tier: 1 | 2 | 3): number {
  const range = aff.tiers[tier - 1];
  const raw = range.min + Math.random() * (range.max - range.min);
  if (!range.step || range.step <= 0) return roundTo(raw, 4);
  const steps = Math.round((raw - range.min) / range.step);
  return roundTo(range.min + steps * range.step, 4);
}

/**
 * Rolea N afijos compatibles con el slot dado, devolviendo la forma
 * estructurada `RolledAffixes`.
 *
 * Para `count === 1`: 1 afijo con 1 componente.
 * Para `count === 2`: 2 afijos. Si el RNG selecciona dos veces el MISMO
 * `modifierKey`, los componentes se MERGEAN en una sola entrada con tiers
 * DISTINTOS (el segundo componente se fuerza a un tier ≠ del primero).
 */
export function rollAffixes(slot: string | undefined, count: number): RolledAffixes {
  if (count <= 0) return {};

  const eligible = AFFIX_POOL.filter((aff) => {
    if (!aff.allowedSlots || aff.allowedSlots.length === 0) return true;
    if (!slot) return false;
    return aff.allowedSlots.includes(slot);
  });

  if (eligible.length === 0) return {};

  const result: RolledAffixes = {};

  for (let i = 0; i < count; i += 1) {
    const aff = pickWeightedAffix(eligible);
    // Los afijos de Reino se keyean por su `id` (no por modifierKey, que comparten
    // todos = 'damageVsKingdom') para no colisionar entre Reinos distintos.
    const key = affixRolledKey(aff);
    const existing = result[key];

    if (existing && existing.length > 0) {
      // Colisión: forzar tier distinto del primer componente.
      const usedTier = existing[0].t;
      const tier = pickTierExcluding(aff, usedTier);
      const value = rollAffixValueForTier(aff, tier);
      existing.push({ v: value, t: tier });
    } else {
      const tier = rollAffixTier(aff);
      const value = rollAffixValueForTier(aff, tier);
      result[key] = [{ v: value, t: tier }];
    }
  }

  return result;
}

/**
 * Elige un afijo del conjunto elegible ponderando por `aff.weight` (default 1). Con
 * todos los pesos = 1 colapsa a la selección uniforme original. Se usa para que los
 * afijos por Reino (`weight = KINGDOM_AFFIX_WEIGHT`) no diluyan el pool de accesorios.
 */
function pickWeightedAffix(eligible: AffixDefinition[]): AffixDefinition {
  let total = 0;
  for (const aff of eligible) total += aff.weight ?? 1;
  let r = Math.random() * total;
  for (const aff of eligible) {
    r -= aff.weight ?? 1;
    if (r < 0) return aff;
  }
  return eligible[eligible.length - 1];
}

/**
 * Elige un tier (1/2/3) usando los pesos del afijo pero EXCLUYENDO el tier
 * dado. Garantiza no devolver `excludeTier`. `excludeTier` puede ser cualquier
 * número (proveniente de un `RolledAffixComponent.t` loose); si no coincide
 * con 1/2/3 el filtro deja los 3 candidatos.
 */
function pickTierExcluding(aff: AffixDefinition, excludeTier: number): 1 | 2 | 3 {
  const weights = aff.tierWeights ?? TIER_DEFAULT_WEIGHTS;
  const tiers: Array<1 | 2 | 3> = [1, 2, 3];
  const filtered = tiers.filter((t) => t !== excludeTier);
  if (filtered.length === 0) return 1; // sólo posible si excludeTier no era 1/2/3
  if (filtered.length === 1) return filtered[0];
  const filteredWeights = filtered.map((t) => weights[t - 1]);
  const total = filteredWeights[0] + filteredWeights[1];
  const r = Math.random() * total;
  return r < filteredWeights[0] ? filtered[0] : filtered[1];
}

/**
 * Flatten de `RolledAffixes` (estructurado por componentes) a `ItemModifiers`
 * (números planos). Suma los `v` de todos los componentes de cada clave.
 *
 * Ej: `{ hp: [{v:10,t:1},{v:25,t:2}] }` → `{ hp: 35 }`.
 *
 * Usado por el merge de stats equipados en `BaseEntity.applyModifiers`.
 */
export function rolledAffixesToModifiers(rolled: RolledAffixes): ItemModifiers {
  const out: ItemModifiers = {};
  for (const [k, components] of Object.entries(rolled)) {
    let total = 0;
    for (const c of components) total += c.v;
    // Afijos de Reino: la key es el id del afijo → rutear a la estructura anidada
    // correspondiente (damageVsKingdom / magicFindVsKingdom) en vez de una key plana.
    const info = KINGDOM_AFFIX_BY_ID.get(k);
    if (info) {
      const nested = (out[info.nestedKey] = out[info.nestedKey] ?? {});
      nested[info.kingdom] = (nested[info.kingdom] ?? 0) + total;
      continue;
    }
    // Afijos por tipo de arma: rutear a damageWithWeapon[subtype] (anidado).
    const sub = WEAPON_AFFIX_BY_ID.get(k);
    if (sub) {
      const nested = (out.damageWithWeapon = out.damageWithWeapon ?? {});
      nested[sub] = (nested[sub] ?? 0) + total;
      continue;
    }
    (out as Record<string, number>)[k] = total;
  }
  return out;
}

function roundTo(value: number, decimals: number): number {
  const f = 10 ** decimals;
  return Math.round(value * f) / f;
}

/**
 * Devuelve la cantidad de afijos que corresponde a una rareza rolled.
 * Wrapper sobre `RARITY_AFFIX_COUNT` por conveniencia.
 */
export function affixCountForRarity(rarity: ItemRarity): number {
  return RARITY_AFFIX_COUNT[rarity] ?? 0;
}

/**
 * Busca una definición de afijo por la clave de modificador. Útil para
 * formatear tooltips desde los rolledAffixes persistidos.
 */
export function findAffixByModifierKey(key: keyof ItemModifiers): AffixDefinition | undefined {
  return AFFIX_POOL.find((aff) => aff.modifierKey === key);
}

/**
 * Busca un afijo por su `id`. Necesario para los afijos de Reino, cuyos
 * `RolledAffixes` se keyean por id (comparten el mismo `modifierKey`), así que
 * `findAffixByModifierKey` no los distingue. Ver docs/CREATURE_KINGDOMS.md.
 */
export function findAffixById(id: string): AffixDefinition | undefined {
  return AFFIX_POOL.find((aff) => aff.id === id);
}

/**
 * Key con que un afijo aparece en `RolledAffixes` (y por ende en las opciones de
 * filtro / score del mercado): su `id` para los afijos de Reino (comparten
 * `modifierKey`), o el `modifierKey` plano para el resto. Fuente única de esta
 * regla; usada en `rollAffixes` y en el `MarketService`. Ver docs/CREATURE_KINGDOMS.md.
 */
export function affixRolledKey(aff: AffixDefinition): string {
  // Reino y arma comparten modifierKey entre sus miembros → se keyean por id.
  return aff.kingdom || aff.weaponSubtype ? aff.id : (aff.modifierKey as string);
}
